import React, { useState, useEffect } from "react";
import { Autocomplete } from "@react-google-maps/api";
import axios from "axios";
import "./AINotifications.css";
import { API_URL } from "../config.js";
import TRUCK_TYPES from "../assets/truck_types.js";
import STATE_NAME_TO_CODE from "../assets/state_name_to_code.js";

const AINotifications = () => {
  const [preferences, setPreferences] = useState({
    is_active: true, // Set default to true
    text_active: false, // Default to false for text messages
    phone_number: "",
    truck_type: "V",
    origin_city: "",
    origin_state: "",
    max_weight: 45000,
    min_rate_per_mile: 0,
    max_deadhead: 0,
    user_id: JSON.parse(localStorage.getItem("user"))?.email || "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [originAutocomplete, setOriginAutocomplete] = useState(null);

  const onOriginLoad = (autocomplete) => {
    setOriginAutocomplete(autocomplete);
  };

  const onOriginPlaceChanged = () => {
    if (!originAutocomplete) return;
    const place = originAutocomplete.getPlace();
    if (!place || !place.address_components) return;

    let city = "",
      state = "";
    for (const component of place.address_components) {
      if (component.types.includes("locality")) city = component.long_name;
      if (component.types.includes("administrative_area_level_1"))
        state = component.short_name;
    }

    if (city && state) {
      setPreferences((prev) => ({
        ...prev,
        origin_city: city,
        origin_state: state,
      }));
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      return;
    }

    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
          );
          if (!response.ok) throw new Error("Failed to get location details");
          const data = await response.json();
          const address = data.address;

          let stateCode = address.state_code || "";
          if (
            !stateCode &&
            address.state &&
            STATE_NAME_TO_CODE[address.state]
          ) {
            stateCode = STATE_NAME_TO_CODE[address.state];
          }

          setPreferences((prev) => ({
            ...prev,
            origin_city: address.city || address.town || address.village || "",
            origin_state: stateCode,
          }));
        } catch (err) {
          setError("Error getting location details: " + err.message);
        }
      },
      (error) => {
        setError("Error getting location: " + error.message);
      },
    );
  };

  // Load existing preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const response = await axios.get(`${API_URL}/voice-preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("response", response);

        // Ensure we have the current user_id and text_active in the loaded preferences
        const userData = JSON.parse(localStorage.getItem("user"));
        setPreferences({
          ...response.data,
          user_id: userData?.email || "",
          text_active: response.data.text_active ?? false, // Ensure text_active has a default value
        });
      } catch (err) {
        console.error("Failed to load preferences:", err);
      }
    };
    loadPreferences();
  }, []);

  const validateForm = () => {
    // Check location fields specifically
    if (!preferences.origin_city || !preferences.origin_state) {
      setError(
        "Click the location icon next to the city field to set your current location automatically.",
      );
      return false;
    }

    // Check user_id separately since it's not user-fillable
    if (!preferences.user_id) {
      setError("User ID not found. Please try logging in again.");
      return false;
    }

    // Check remaining required fields
    const requiredFields = ["truck_type"];
    const emptyFields = requiredFields.filter((field) => !preferences[field]);

    if (emptyFields.length > 0) {
      setError(
        `Please fill in all required fields: ${emptyFields.join(", ").replace(/_/g, " ")}`,
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate form before submitting
    if (!validateForm()) {
      setLoading(false);
      return;
    }
    preferences.is_active = true;
    console.log("preferences", preferences);
    try {
      const token = localStorage.getItem("access_token");

      await axios.post(`${API_URL}/voice-preferences`, preferences, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      // Handle validation errors
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          setError(
            err.response.data.detail.map((error) => error.msg).join(", "),
          );
        } else if (typeof err.response.data.detail === "object") {
          setError(Object.values(err.response.data.detail).join(", "));
        } else {
          setError(err.response.data.detail);
        }
      } else {
        setError("Failed to save preferences");
      }
    } finally {
      setLoading(false);
    }

    {
      const user = JSON.parse(localStorage.getItem("user"));
      fetch(" https://cinesis.app.n8n.cloud/webhook/send_text", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: user.email,
        }),
      })
        .then((response) => {
          if (!response.ok) throw new Error("Failed to trigger notifications");
          alert(
            "Your first AI load notifications are on the way to your email.\nYou'll get more updates every few hours.",
          );
        })
        .catch((err) => {
          console.error("Error triggering notifications:", err);
          alert("Failed to trigger notifications. Please try again.");
        });
    }
  };

  const handleChange = (e) => {
    console.log("e", e);
    const { name, value, type, checked } = e.target;
    setPreferences((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
            ? parseFloat(value) || 0
            : value,
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  return (
    <div className="ai-notifications-container">
      <div className="main-content">
        <div className="preferences-form-container">
          <div className="form-header">
            <h2>AI Notifications Preferences</h2>
            <p>
              We'll automatically notify you by email and text about matching
              loads.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="preferences-form" noValidate>
            <div className="form-group">
              <label htmlFor="phone_number">Phone Number</label>
              <div className="input-with-icon">
                <i className="fas fa-phone"></i>
                <input
                  id="phone_number"
                  name="phone_number"
                  type="tel"
                  value={preferences.phone_number}
                  onChange={handleChange}
                  placeholder="(555) 555-5555"
                />
              </div>
              <div style={{ marginTop: "10px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="text_active"
                    checked={preferences.text_active}
                    onChange={handleChange}
                  />
                  <span>Receive text message notifications</span>
                </label>
                <div
                  style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}
                >
                  By enabling text notifications, you agree to receive SMS
                  updates from Cinesis about truck loads. Message frequency
                  varies. Msg & data rates may apply. Reply STOP to unsubscribe.
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="truck_type">Truck Type</label>
              <div className="input-with-icon">
                <i className="fas fa-truck"></i>
                <select
                  id="truck_type"
                  name="truck_type"
                  value={preferences.truck_type}
                  onChange={handleChange}
                >
                  {TRUCK_TYPES.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="origin_city">Origin City</label>
              <div className="input-with-icon">
                <i className="fas fa-city"></i>
                <div style={{ width: "100%" }}>
                  <Autocomplete
                    onLoad={onOriginLoad}
                    onPlaceChanged={onOriginPlaceChanged}
                    restrictions={{ country: "us" }}
                  >
                    <input
                      id="origin_city"
                      name="origin_city"
                      type="text"
                      value={preferences.origin_city}
                      onChange={handleChange}
                      placeholder="Enter city"
                      required
                      style={{ paddingRight: "40px", width: "100%" }}
                    />
                  </Autocomplete>
                  <div
                    onClick={getCurrentLocation}
                    style={{
                      position: "absolute",
                      right: "30px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "var(--primary)",
                      padding: "8px",
                      borderRadius: "50%",
                      transition: "background-color 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title="Use current location"
                  >
                    <i
                      className="fas fa-location-arrow"
                      style={{
                        color: "var(--primary) !important",
                        opacity: "1",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="origin_state">Origin State</label>
              <div className="input-with-icon">
                <i className="fas fa-map"></i>
                <input
                  id="origin_state"
                  name="origin_state"
                  type="text"
                  value={preferences.origin_state}
                  onChange={handleChange}
                  placeholder="Enter state (e.g., CA)"
                  required
                  maxLength="2"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="max_weight">Maximum Weight (lbs)</label>
              <div className="input-with-icon">
                <i className="fas fa-dumbbell"></i>
                <input
                  id="max_weight"
                  name="max_weight"
                  type="number"
                  value={preferences.max_weight}
                  onChange={handleChange}
                  placeholder="Enter maximum weight (lbs)"
                  min="0"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="min_rate_per_mile">
                Minimum Rate per Mile ($)
              </label>
              <div className="input-with-icon">
                <i className="fas fa-dollar-sign"></i>
                <input
                  id="min_rate_per_mile"
                  name="min_rate_per_mile"
                  type="number"
                  step="0.1"
                  value={preferences.min_rate_per_mile}
                  onChange={handleChange}
                  placeholder="Enter minimum rate"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="max_deadhead">Maximum Deadhead (miles)</label>
              <div className="input-with-icon">
                <i className="fas fa-road"></i>
                <input
                  id="max_deadhead"
                  name="max_deadhead"
                  type="number"
                  value={preferences.max_deadhead}
                  onChange={handleChange}
                  placeholder="Enter max deadhead"
                />
              </div>
            </div>

            <button type="submit" disabled={loading} className="submit-button">
              {loading ? "Saving..." : "Save Preferences"}
            </button>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            {success && (
              <div className="success-message">
                <i className="fas fa-check-circle"></i>
                Preferences saved successfully!
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AINotifications;
